// QR code generation for Senegalese e-invoicing verification
// Uses a canvas-based QR encoding (no external package dependency)

import type { Invoice } from "./types";
import { getCompanyDetails } from "./invoice-service";

/**
 * Generates a verification URL for the given invoice number.
 * In production this would point to a real verification endpoint.
 */
export function generateVerificationUrl(invoiceNumber: string): string {
  const encoded = encodeURIComponent(invoiceNumber);
  return `https://djayma.com/verify?invoice=${encoded}`;
}

/**
 * Builds the QR payload string containing invoice verification data.
 */
function buildQrPayload(invoice: Invoice): string {
  const config = getCompanyDetails();
  const payload = {
    invoice: invoice.number,
    date: invoice.date.slice(0, 10),
    total: invoice.total,
    currency: invoice.currency,
    ninea: config.ninea,
    verify: generateVerificationUrl(invoice.number),
  };
  return JSON.stringify(payload);
}

// ---------------------------------------------------------------------------
// Minimal QR code generator (alphanumeric, canvas-based)
//
// For production use, swap this with the `qrcode` npm package.
// This placeholder renders a styled SVG data-URI that displays the invoice
// number in a scannable-looking frame, suitable for development & print.
// ---------------------------------------------------------------------------

function escapeXml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Generates a QR code data URL for the invoice.
 *
 * Current implementation: produces an SVG data-URI placeholder that displays
 * the invoice number and a grid pattern. Replace with a real QR library
 * (e.g. `qrcode` package) before production launch.
 */
export function generateInvoiceQR(invoice: Invoice): string {
  const payload = buildQrPayload(invoice);
  const label = invoice.number;

  // Deterministic pseudo-random grid from payload hash
  const cells = 21; // QR version 1 is 21x21
  const cellSize = 8;
  const size = cells * cellSize;
  const margin = 16;
  const totalSize = size + margin * 2;

  let hash = 0;
  for (let i = 0; i < payload.length; i++) {
    hash = ((hash << 5) - hash + payload.charCodeAt(i)) | 0;
  }

  const rects: string[] = [];

  // Generate a grid pattern seeded by the payload hash
  for (let row = 0; row < cells; row++) {
    for (let col = 0; col < cells; col++) {
      const isFinderPattern =
        (row < 7 && col < 7) ||
        (row < 7 && col >= cells - 7) ||
        (row >= cells - 7 && col < 7);

      const isFinderBorder =
        isFinderPattern &&
        (row === 0 ||
          row === 6 ||
          col === 0 ||
          col === 6 ||
          (row >= 2 && row <= 4 && col >= 2 && col <= 4) ||
          (row < 7 && col >= cells - 7 && (col === cells - 7 || col === cells - 1)) ||
          (row >= cells - 7 && col < 7 && (row === cells - 7 || row === cells - 1)));

      // Seeded bit for data cells
      const seed = ((hash + row * 31 + col * 17) >>> 0) % 3;
      const filled = isFinderBorder || (!isFinderPattern && seed === 0);

      if (filled) {
        const x = margin + col * cellSize;
        const y = margin + row * cellSize;
        rects.push(
          `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" fill="#1a1a1a"/>`,
        );
      }
    }
  }

  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${totalSize}" height="${totalSize + 24}" viewBox="0 0 ${totalSize} ${totalSize + 24}">`,
    `<rect width="${totalSize}" height="${totalSize + 24}" fill="white"/>`,
    ...rects,
    `<text x="${totalSize / 2}" y="${totalSize + 16}" text-anchor="middle" font-family="monospace" font-size="10" fill="#333">${escapeXml(label)}</text>`,
    `</svg>`,
  ].join("");

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
